<template>
  <BaseCombobox
    ref="comboboxRef"
    class="order-item-inventory-combobox"
    :items="options"
    :search-term="searchTerm"
    :loading="loading"
    value-key="value"
    label-key="label"
    trigger-mode="input"
    filter-mode="external"
    :placeholder="placeholder"
    :disabled-values="excludedItemIds"
    no-results-text="No inventory found"
    loading-text="Searching inventory..."
    aria-label="Inventory search"
    :show-open-button="false"
    @update:search-term="handleSearchTermUpdate"
    @focus="handleFocus"
    @select="handleSelect"
  >
    <template #option="{ option, disabled }">
      <span class="is-flex is-flex-direction-column">
        <span>{{ option.label }}</span>
        <span v-if="option.ingredientName" class="has-text-grey is-size-7">{{
          option.ingredientName
        }}</span>
      </span>
      <span v-if="disabled" class="tag is-light ml-2">Already in order</span>
    </template>
    <template v-if="supplierId" #no-results>
      No inventory found for this supplier.
      <a class="has-text-link" @pointerdown.prevent="clearSupplierFilter">Search all inventory</a>
    </template>
  </BaseCombobox>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onMounted, onUnmounted, watch } from "vue";
  import { useDebounceFn } from "@vueuse/core";

  import BaseCombobox from "./shared/base-combobox.vue";
  import { useInventorySearch } from "./stores/inventory-search-store";

  const props = defineProps({
    userId: { type: Number, required: true },
    supplierId: { type: Number, default: null },
    placeholder: { type: String, default: "Search inventory..." },
    autofocus: { type: Boolean, default: false },
    excludedItemIds: { type: Array, default: () => [] },
  });

  const emit = defineEmits(["select"]);

  const { results, loading, search, clear } = useInventorySearch();
  const comboboxRef = _ref(null);
  let searchTerm = _ref("");

  const options = _computed(() =>
    results.value.map((inventory) => ({
      value: inventory.id,
      label: inventory.custom_name || inventory.ingredient.name,
      ingredientName: inventory.custom_name ? inventory.ingredient.name : null,
      unit: inventory.unit,
      raw: inventory,
    }))
  );

  const debouncedSearch = useDebounceFn((query) => {
    search(props.userId, query, { supplierId: props.supplierId, excludeSubrecipes: true });
  }, 300);

  function handleSearchTermUpdate(query) {
    searchTerm.value = query;
    loading.value = true;
    debouncedSearch(query);
  }

  function handleFocus() {
    loading.value = true;
    debouncedSearch(searchTerm.value);
  }

  watch(
    () => props.supplierId,
    () => {
      loading.value = true;
      debouncedSearch(searchTerm.value);
    }
  );

  function clearSupplierFilter() {
    document.dispatchEvent(new CustomEvent("clear-supplier-filter"));
  }

  function handleSelect(option) {
    clear();
    debouncedSearch.cancel?.();
    searchTerm.value = "";
    emit("select", option.raw);
  }

  function focus() {
    comboboxRef.value?.focusTrigger();
  }

  onMounted(() => {
    if (props.autofocus) focus();
  });

  onUnmounted(() => {
    debouncedSearch.cancel?.();
    clear();
  });

  defineExpose({ focus });
</script>

<style lang="scss" scoped>
  .order-item-inventory-combobox {
    width: 100%;
  }
</style>
